/**
 * Notification Dropdown Component
 * Popup panel showing recent notifications
 */

import React from "react";
import { useNotifications } from "@/contexts/NotificationContext";
import NotificationItem from "./NotificationItem";
import { CheckCheck, Settings, ExternalLink, Bell } from "lucide-react";
import { useRouter } from "next/navigation";

interface NotificationDropdownProps {
    onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
    const { notifications, unreadCount, markAllAsRead, isLoading } = useNotifications();
    const recentNotifications = notifications.slice(0, 5);
    const router = useRouter();

    const handleMarkAllRead = async () => {
        await markAllAsRead();
    };

    const handleViewAll = () => {
        onClose();
        router.push("/dashboard/notifications");
    };


    return (
        <div
            className="
                w-[380px] max-w-[90vw] 
                bg-popover 
                border border-border/50 rounded-2xl shadow-2xl 
                overflow-hidden animate-in fade-in zoom-in-95 duration-200
                ring-1 ring-black/5 dark:ring-white/5
            "
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/50 bg-muted/20">
                <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-foreground tracking-tight">Notifications</h3>
                    {unreadCount.total > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-bold border border-primary/20 shadow-xs">
                            {unreadCount.total} NEW
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {unreadCount.total > 0 && (
                        <button
                            className="p-1.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200 active:scale-95"
                            onClick={handleMarkAllRead}
                            title="Mark all as read"
                        >
                            <CheckCheck size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="max-h-[420px] overflow-y-auto scrollbar-thin scrollbar-thumb-border/50 scrollbar-track-transparent">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4 text-muted-foreground">
                        <div className="h-4 w-4 rounded-full bg-primary/20 animate-ping" />
                        <p className="text-xs font-medium tracking-wide text-muted-foreground/80">UPDATING...</p>
                    </div>
                ) : recentNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-4">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-muted/50 to-muted/80 flex items-center justify-center shadow-inner border border-white/10 dark:border-white/5">
                            <Bell className="w-8 h-8 text-muted-foreground/40" />
                        </div>
                        <div className="space-y-1">
                            <p className="text-base font-medium text-foreground">All caught up!</p>
                            <p className="text-xs text-muted-foreground max-w-[200px] mx-auto leading-relaxed">
                                No new notifications at the moment. Check back later for updates.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col">
                        {recentNotifications.map((notification) => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onClick={onClose}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-border/50 bg-muted/20 backdrop-blur-sm">
                <button
                    className="
                        w-full flex items-center justify-center gap-2 py-2.5 
                        text-xs font-semibold text-muted-foreground hover:text-primary 
                        rounded-xl hover:bg-primary/5 transition-all duration-200 group
                    "
                    onClick={handleViewAll}
                >
                    View all interaction history
                    <ExternalLink size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                </button>
            </div>
        </div>
    );
};

export default NotificationDropdown;
